<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import TeamPerformanceAPI from 'dashboard/api/teamPerformance';
import { createLatestRequest } from 'dashboard/helper/latestRequest';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const funnels = useMapGetter('funnels/getFunnels');
const latestRequest = createLatestRequest();

const isLoading = ref(false);
const report = ref(null);
const selectedPeriod = ref('30d');
const customStartDate = ref('');
const customEndDate = ref('');
const selectedFunnelIds = ref([]);

const funnelOptions = computed(() =>
  (funnels.value || []).map(funnel => ({ id: funnel.id, name: funnel.name }))
);

const selectedFunnels = computed({
  get: () =>
    funnelOptions.value.filter(funnel =>
      selectedFunnelIds.value.includes(funnel.id)
    ),
  set: value => {
    selectedFunnelIds.value = value ? value.map(funnel => funnel.id) : [];
  },
});

const periodOptions = computed(() => [
  { value: 'today', label: t('CRM_METRICS.TEAM_PERFORMANCE.PERIOD_TODAY') },
  { value: '7d', label: t('CRM_METRICS.TEAM_PERFORMANCE.PERIOD_7D') },
  { value: '30d', label: t('CRM_METRICS.TEAM_PERFORMANCE.PERIOD_30D') },
  { value: 'month', label: t('CRM_METRICS.TEAM_PERFORMANCE.PERIOD_MONTH') },
  { value: 'custom', label: t('CRM_METRICS.TEAM_PERFORMANCE.PERIOD_CUSTOM') },
]);

const formatLocalDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateRange = computed(() => {
  const end = new Date();
  const start = new Date();

  if (selectedPeriod.value === 'custom') {
    return {
      start_date: customStartDate.value,
      end_date: customEndDate.value,
    };
  }
  if (selectedPeriod.value === 'month') {
    start.setDate(1);
  } else {
    start.setDate(
      start.getDate() - { today: 0, '7d': 7, '30d': 30 }[selectedPeriod.value]
    );
  }
  return {
    start_date: formatLocalDate(start),
    end_date: formatLocalDate(end),
  };
});

const canLoad = computed(
  () =>
    selectedPeriod.value !== 'custom' ||
    (customStartDate.value && customEndDate.value)
);

const loadReport = async () => {
  if (!canLoad.value) return;

  const requestId = latestRequest.next();
  isLoading.value = true;
  try {
    const response = await TeamPerformanceAPI.get({
      ...dateRange.value,
      funnel_ids: selectedFunnelIds.value,
    });
    if (!latestRequest.isLatest(requestId)) return;
    report.value = response.data;
  } catch (error) {
    if (!latestRequest.isLatest(requestId)) return;
    report.value = null;
    useAlert(t('CRM_METRICS.TEAM_PERFORMANCE.ERROR'));
  } finally {
    if (latestRequest.isLatest(requestId)) isLoading.value = false;
  }
};

const formatMoney = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value || 0
  );
const formatPercent = value => `${Number(value || 0).toLocaleString('pt-BR')}%`;

const summary = computed(() => report.value?.summary || {});
const sellerRows = computed(() => report.value?.sellers?.rows || []);
const unattributed = computed(() => report.value?.sellers?.unattributed || {});
const hasUnattributed = computed(
  () =>
    (unattributed.value.sales || 0) +
      (unattributed.value.losses || 0) +
      (unattributed.value.sales_value || 0) >
    0
);
const sdrRows = computed(() => report.value?.sdrs?.rows || []);
const appointmentsTotal = computed(() =>
  sdrRows.value.reduce((sum, row) => sum + (row.appointments || 0), 0)
);

const sumOf = (rows, key) =>
  rows.reduce((sum, row) => sum + (row[key] || 0), 0);
const sellersTotal = computed(() => ({
  sales: sumOf(sellerRows.value, 'sales') + (unattributed.value.sales || 0),
  sales_value:
    sumOf(sellerRows.value, 'sales_value') +
    (unattributed.value.sales_value || 0),
  losses: sumOf(sellerRows.value, 'losses') + (unattributed.value.losses || 0),
}));

const goalFor = userId =>
  (report.value?.goals || []).find(goal => goal.user_id === userId);
const goalPercent = goal => {
  if (!goal) return 0;
  if (goal.target_value > 0) {
    return Math.min((goal.current_value / goal.target_value) * 100, 100);
  }
  return Math.min(goal.progress_percentage || 0, 100);
};

const summaryCards = computed(() => [
  ['SALES', summary.value.sales || 0],
  ['SALES_VALUE', formatMoney(summary.value.sales_value)],
  ['WIN_RATE', formatPercent(summary.value.win_rate)],
  ['AVERAGE_TICKET', formatMoney(summary.value.average_ticket)],
  ['LEADS', summary.value.leads || 0],
  ['LEAD_CONVERSION', formatPercent(summary.value.lead_conversion)],
  ['APPOINTMENTS', appointmentsTotal.value],
]);

const sellerColumns = [
  'SALES',
  'SALES_VALUE',
  'LOSSES',
  'WIN_RATE',
  'AVERAGE_TICKET',
  'GOAL',
];
const sdrColumns = [
  'APPOINTMENTS',
  'MEETINGS_HELD',
  'MEETINGS_NO_SHOW',
  'MEETINGS_UNMARKED',
  'SDR_SALES',
  'SDR_SALES_VALUE',
];
const sdrMoneyColumns = ['sdr_sales_value'];

watch(
  [selectedPeriod, customStartDate, customEndDate, selectedFunnelIds],
  loadReport
);
onMounted(async () => {
  if (!funnels.value?.length) store.dispatch('funnels/get');
  await loadReport();
});

const sdrCell = (row, column) => {
  const key = column.toLowerCase();
  const value = row[key] || 0;
  return sdrMoneyColumns.includes(key) ? formatMoney(value) : value;
};
</script>

<template>
  <div class="min-h-full bg-n-background p-6">
    <div class="mb-6">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('CRM_METRICS.TEAM_PERFORMANCE.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-10">
        {{ t('CRM_METRICS.TEAM_PERFORMANCE.DESCRIPTION') }}
      </p>
    </div>

    <div
      class="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
    >
      <div class="min-w-48 flex-1">
        <label class="mb-1 block text-sm font-medium text-n-slate-11">
          {{ t('CRM_METRICS.TEAM_PERFORMANCE.PERIOD') }}
        </label>
        <select
          v-model="selectedPeriod"
          class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
        >
          <option
            v-for="option in periodOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
      <template v-if="selectedPeriod === 'custom'">
        <div class="min-w-40 flex-1">
          <label class="mb-1 block text-sm font-medium text-n-slate-11">
            {{ t('CRM_METRICS.TEAM_PERFORMANCE.START_DATE') }}
          </label>
          <input
            v-model="customStartDate"
            type="date"
            class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
          />
        </div>
        <div class="min-w-40 flex-1">
          <label class="mb-1 block text-sm font-medium text-n-slate-11">
            {{ t('CRM_METRICS.TEAM_PERFORMANCE.END_DATE') }}
          </label>
          <input
            v-model="customEndDate"
            type="date"
            class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
          />
        </div>
      </template>
      <div class="min-w-64 flex-[2] multiselect-wrap--small">
        <label class="mb-1 block text-sm font-medium text-n-slate-11">
          {{ t('CRM_METRICS.TEAM_PERFORMANCE.FUNNELS') }}
        </label>
        <multiselect
          v-model="selectedFunnels"
          :options="funnelOptions"
          :placeholder="t('CRM_METRICS.TEAM_PERFORMANCE.ALL_FUNNELS')"
          label="name"
          track-by="id"
          multiple
          :max-height="200"
          selected-label=""
          :select-label="t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
        />
      </div>
      <Button
        icon="i-lucide-refresh-cw"
        :is-loading="isLoading"
        slate
        faded
        @click="loadReport"
      >
        {{ t('DEAL_REPORTS.REFRESH') }}
      </Button>
    </div>

    <div
      v-if="isLoading && !report"
      class="flex justify-center py-20 text-n-slate-10"
    >
      {{ t('CRM_METRICS.TEAM_PERFORMANCE.LOADING') }}
    </div>
    <div
      v-else-if="!report"
      class="flex justify-center py-20 text-n-slate-10"
      data-test="empty"
    >
      {{ t('CRM_METRICS.TEAM_PERFORMANCE.ERROR') }}
    </div>
    <template v-else>
      <h2 class="mb-3 text-base font-semibold text-n-slate-12">
        {{ t('CRM_METRICS.TEAM_PERFORMANCE.SUMMARY') }}
      </h2>
      <div class="mb-8 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <div
          v-for="[key, value] in summaryCards"
          :key="key"
          class="flex min-h-24 flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4"
          :data-test="`card-${key}`"
        >
          <div class="flex justify-end">
            <button
              v-tooltip.top="t(`CRM_METRICS.${key}.HELP`)"
              type="button"
              class="cursor-help rounded text-n-slate-9 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-8"
              :aria-label="t(`CRM_METRICS.${key}.HELP`)"
            >
              <span class="i-lucide-circle-help block text-base" />
            </button>
          </div>
          <p class="mt-auto pt-2 text-2xl font-semibold text-n-slate-12">
            {{ value }}
          </p>
          <p class="mt-1 text-sm text-n-slate-10">
            {{ t(`CRM_METRICS.${key}.NAME`) }}
          </p>
        </div>
      </div>

      <h2 class="mb-3 text-base font-semibold text-n-slate-12">
        {{ t('CRM_METRICS.TEAM_PERFORMANCE.SELLERS') }}
      </h2>
      <div
        class="mb-8 overflow-x-auto rounded-xl border border-n-weak bg-n-solid-2"
      >
        <table class="w-full text-left text-sm text-n-slate-12">
          <thead class="border-b border-n-weak text-n-slate-11">
            <tr>
              <th class="p-3 font-medium">
                {{ t('CRM_METRICS.TEAM_PERFORMANCE.NAME') }}
              </th>
              <th
                v-for="column in sellerColumns"
                :key="column"
                class="p-3 font-medium"
              >
                <span v-tooltip.top="t(`CRM_METRICS.${column}.HELP`)">
                  {{ t(`CRM_METRICS.${column}.NAME`) }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in sellerRows"
              :key="row.user_id"
              class="border-b border-n-weak"
              data-test="seller-row"
            >
              <td class="p-3">{{ row.name }}</td>
              <td class="p-3">{{ row.sales }}</td>
              <td class="p-3">{{ formatMoney(row.sales_value) }}</td>
              <td class="p-3">{{ row.losses }}</td>
              <td class="p-3">{{ formatPercent(row.win_rate) }}</td>
              <td class="p-3">{{ formatMoney(row.average_ticket) }}</td>
              <td class="min-w-40 p-3">
                <template v-if="goalFor(row.user_id)">
                  <div class="h-2 overflow-hidden rounded-full bg-n-alpha-2">
                    <div
                      class="h-full rounded-full bg-n-blue-9"
                      :style="{
                        width: `${goalPercent(goalFor(row.user_id))}%`,
                      }"
                    />
                  </div>
                  <p class="mt-1 text-xs text-n-slate-10">
                    {{
                      t('CRM_METRICS.TEAM_PERFORMANCE.GOAL_PROGRESS', {
                        current: formatMoney(
                          goalFor(row.user_id).current_value
                        ),
                        target: formatMoney(goalFor(row.user_id).target_value),
                      })
                    }}
                  </p>
                </template>
              </td>
            </tr>
            <tr
              v-if="hasUnattributed"
              class="border-b border-n-weak"
              data-test="unattributed-row"
            >
              <td class="p-3">
                <span
                  v-tooltip.top="
                    t('CRM_METRICS.TEAM_PERFORMANCE.UNATTRIBUTED_HELP')
                  "
                >
                  {{ t('CRM_METRICS.TEAM_PERFORMANCE.UNATTRIBUTED') }}
                </span>
              </td>
              <td class="p-3">{{ unattributed.sales || 0 }}</td>
              <td class="p-3">{{ formatMoney(unattributed.sales_value) }}</td>
              <td class="p-3">{{ unattributed.losses || 0 }}</td>
              <td class="p-3" colspan="3" />
            </tr>
          </tbody>
          <tfoot class="font-semibold">
            <tr data-test="sellers-total">
              <td class="p-3">{{ t('CRM_METRICS.TEAM_PERFORMANCE.TOTAL') }}</td>
              <td class="p-3" data-test="total-sales">
                {{ sellersTotal.sales }}
              </td>
              <td class="p-3">{{ formatMoney(sellersTotal.sales_value) }}</td>
              <td class="p-3">{{ sellersTotal.losses }}</td>
              <td class="p-3" colspan="3" />
            </tr>
          </tfoot>
        </table>
      </div>

      <h2 class="mb-3 text-base font-semibold text-n-slate-12">
        {{ t('CRM_METRICS.TEAM_PERFORMANCE.SDRS') }}
      </h2>
      <div class="overflow-x-auto rounded-xl border border-n-weak bg-n-solid-2">
        <table class="w-full text-left text-sm text-n-slate-12">
          <thead class="border-b border-n-weak text-n-slate-11">
            <tr>
              <th class="p-3 font-medium">
                {{ t('CRM_METRICS.TEAM_PERFORMANCE.NAME') }}
              </th>
              <th
                v-for="column in sdrColumns"
                :key="column"
                class="p-3 font-medium"
              >
                <span v-tooltip.top="t(`CRM_METRICS.${column}.HELP`)">
                  {{ t(`CRM_METRICS.${column}.NAME`) }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in sdrRows"
              :key="row.key"
              class="border-b border-n-weak"
              data-test="sdr-row"
            >
              <td class="p-3">{{ row.name }}</td>
              <td v-for="column in sdrColumns" :key="column" class="p-3">
                {{ sdrCell(row, column) }}
              </td>
            </tr>
            <tr v-if="!sdrRows.length">
              <td class="p-3 text-n-slate-10" :colspan="sdrColumns.length + 1">
                {{ t('CRM_METRICS.TEAM_PERFORMANCE.EMPTY') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
